/**
 * EN-1571: closing the ML Verified Passages window.
 *
 * The window is opened from the 'Show ML Verified Passages' overlay in the pig
 * run selection card (the Leaflet layers control). Removing that overlay's
 * layer from the map makes the control fire overlayremove and untick the
 * checkbox, so closing goes through the same path as the user unticking it.
 * The helpers use only the Leaflet calls below (no Vue), so they can be tested
 * against a real Leaflet layers control in a bare page.
 */

/** Name of the overlay in the pig run selection card. */
export const ML_OVERLAY_NAME = 'Show ML Verified Passages';

/** The part of L.Map the helpers need. */
export interface MLOverlayMap {
  hasLayer(layer: unknown): boolean;
  removeLayer(layer: unknown): unknown;
}

/** The part of L.Control.Layers the helpers need. */
export interface MLOverlayControl {
  addOverlay(layer: unknown, name: string): unknown;
  removeLayer(layer: unknown): unknown;
}

/**
 * Turns the 'Show ML Verified Passages' overlay off if it is on. Call it while
 * the overlay is still in the layers control, so the control unticks it.
 * Returns whether the overlay was on.
 */
export function hideMLOverlay(
  map: MLOverlayMap | null | undefined,
  layer: unknown
): boolean {
  if (!map || !layer || !map.hasLayer(layer)) return false;
  map.removeLayer(layer);
  return true;
}

/**
 * Closes the window: unticks the overlay, whose overlayremove handler resets
 * the panel state, or, when the overlay is not on the map, resets it directly.
 */
export function closeMLOverlayOrPanel(
  map: MLOverlayMap | null | undefined,
  layer: unknown,
  resetPanel: () => void
): void {
  if (!hideMLOverlay(map, layer)) resetPanel();
}

/**
 * Whether the window must close after the live pig runs changed: the run it
 * was opened for (or last showed) has ended or been cancelled, or no live run
 * is left.
 */
export function shouldAutoCloseMLPanel(
  panelPigRunId: number | null,
  livePigRunIds: number[]
): boolean {
  if (livePigRunIds.length === 0) return true;
  return panelPigRunId != null && !livePigRunIds.includes(panelPigRunId);
}

/**
 * The run the open window shows after the selection changed. It follows the
 * selection, except when its remembered run has already left the live view:
 * a removed run clears or auto-selects the selection, and that must not hide
 * the end from the close check, whichever watcher runs first.
 */
export function followSelectedPigRun(
  panelPigRunId: number | null,
  selectedPigRunId: number | null,
  livePigRunIds: number[]
): number | null {
  if (selectedPigRunId == null) return panelPigRunId;
  if (panelPigRunId != null && !livePigRunIds.includes(panelPigRunId)) {
    return panelPigRunId;
  }
  return selectedPigRunId;
}

export interface LiveRunsChange {
  map: MLOverlayMap | null | undefined;
  control: MLOverlayControl | null | undefined;
  layer: unknown;
  panelOpen: boolean;
  /** The run the window remembers (mlPanelPigRunId), not the selection. */
  panelPigRunId: number | null;
  previousLiveCount: number;
  livePigRunIds: number[];
  resetPanel: () => void;
}

/**
 * The run-list watcher's handling of the ML option: first decide and close the
 * window, then add or remove the option, so a closed window's option is
 * unticked before it leaves the card and comes back unticked.
 * Returns whether the window was closed.
 */
export function applyLiveRunsToMLPanel(change: LiveRunsChange): boolean {
  const { map, control, layer, livePigRunIds, previousLiveCount } = change;
  const close =
    change.panelOpen &&
    shouldAutoCloseMLPanel(change.panelPigRunId, livePigRunIds);
  if (close) closeMLOverlayOrPanel(map, layer, change.resetPanel);
  if (control && layer) {
    if (previousLiveCount === 0 && livePigRunIds.length > 0) {
      control.addOverlay(layer, ML_OVERLAY_NAME);
    } else if (previousLiveCount > 0 && livePigRunIds.length === 0) {
      control.removeLayer(layer);
    }
  }
  return close;
}
